import { cloneDeep, merge } from 'lodash-es';
import { CHART_TYPES, X_AXIS_TYPE } from '../enums';

const SUPPORTED_CHART_TYPES = Object.values(CHART_TYPES);

const MAX_DISPLAYED_DATASETS = 3;

const DEFAULT_DATASET_CONFIG = {
  borderRadius: 5,
  pointRadius: 0,
  pointHitRadius: 0,
  pointHoverRadius: 0,
  borderJoinStyle: 'round',
  borderCapStyle: 'round',
  tension: 0.3,
  cubicInterpolationMode: 'monotone',
};

const limitDisplayedDatasets = (dataset, index) => {
  if (index >= MAX_DISPLAYED_DATASETS) {
    dataset.hidden = true;
  }
  return dataset;
};

/**
 * we save dimensionFieldNames on the dataset so later we can grab a specific styling for specific dimension
 * field with a specific value (example - "american express" from the "paymentMethod" will be colored in purple
 * @param {Object} dataset
 * @return {string[]}
 */
const getDimensionFieldNamesFromDataSet = dataset => {
  return dataset?.aggregation?.dimensions?.map(({ key }) => key) || [];
};

/**
 * Builds the x and y model of the chart data.
 * Most of the times, 'X' axis will render the segments, and some times the 'Y' axis will render the segments.
 * setting x or y as '' is for the case we have no dimensions, and each bar is a series on its own
 * @param {Object} item data index value of the segment.
 * @param {'X'|'Y'} segmentAxis represent the axis that will render the segments.
 * @param {boolean} hasDimensions in case we have no dimensions, each bar is a series on its own.
 * @param {string} xAxisType the type of the x axis.
 * @param {string} seriesType the type of the series.
 * @return {{x:(number|string), y:(number|string)}}
 */
const XAndYvalues = (item, segmentAxis, hasDimensions, xAxisType, seriesType) => {
  const isSimpleKPI = (
    (xAxisType === X_AXIS_TYPE.SIMPLE_KPI) &&
    (seriesType !== CHART_TYPES.FUNNEL.toUpperCase())
  );

  const aggregationLabel = isSimpleKPI ? item.aggregationLabel : '';
  const itemLabel = hasDimensions ? item.dimensions[0] : aggregationLabel;
  if (segmentAxis === 'X') {
    return { x: itemLabel, y: item.value };
  }
  return { x: item.value, y: itemLabel };
};

const chartDataAggregationBuilder = (
  data,
  hasDimensions,
  segmentAxis = 'X',
  xAxisType,
  seriesType
) => {
  const chartData = data.map(item => ({
    ...XAndYvalues(item, segmentAxis, hasDimensions, xAxisType, seriesType),
    yFormatted: item.formattedValue,
    aggregationLabel: item.aggregationLabel,
    dimensions: item.dimensions,
    otherBuckets: item.otherBuckets,
    note: item.note,
    color: item.color
  }));

  return chartData;
};

const extractDatasets = config => {
  const { series: allSeries = [], xAxisType } = config;
  const series = allSeries.filter(series => !['KPI', 'CONST'].includes(series.seriesType));

  if (xAxisType === X_AXIS_TYPE.SINGLE_TIME_FIELD_SINGLE_NON_TIME_FIELD) {
    // assumption - only one series in this case
    const [dataset] = series;
    const { seriesType } = dataset;
    const rawDatasets = {};

    dataset.aggregation.data.forEach(dataPoint => {
      const [timeDimension, nonTimeDimension] = dataPoint.dimensions;
      const { value, formattedValue, aggregationLabel, dimensions, note } = dataPoint;
      rawDatasets[nonTimeDimension] = rawDatasets[nonTimeDimension] || [];
      rawDatasets[nonTimeDimension].push({
        x: timeDimension,
        y: value,
        yFormatted: formattedValue,
        aggregationLabel,
        dimensions,
        note
      });
    });

    return Object.entries(rawDatasets).map(([key, value], index) => {
      let data = {
        ...DEFAULT_DATASET_CONFIG,
        type: seriesType.toLowerCase(),
        label: key,
        data: value,
        /**
         * we save dimensionFieldNames on the dataset so later we can grab a specific styling for specific dimension
         * field with a specific value (example - "american express" from the "paymentMethod" will be colored in purple
         */
        dimensionFieldNames: getDimensionFieldNamesFromDataSet(dataset)
      };

      data = merge(data, dataset.style);

      return data;
    });
  }

  return series.map(dataset => {
    const { seriesType, periodOverPeriod, aggregation } = dataset;
    const hasDimensions = (aggregation.dimensions || []).length > 0;
    const segmentAxis = [CHART_TYPES.HORIZONTAL_BAR].includes(seriesType) ? 'Y' : 'X';
    const chartData = chartDataAggregationBuilder(
      aggregation.data || [],
      hasDimensions,
      segmentAxis,
      xAxisType,
      seriesType
    );

    const formatEllipsis = (ctx, text) => {
      const maxLabelLength = Math.round(ctx.element.width / 10);
      return `${text.slice(0, maxLabelLength)}${text.length > maxLabelLength ? '..' : ''}`;
    };

    const formatLabel = ctx => {
      const category = formatEllipsis(ctx, ctx.raw.g.toUpperCase());
      const sortedChartData = chartData.sort((a, b) => b.y - a.y);
      const value = formatEllipsis(ctx, sortedChartData[ctx.index].yFormatted.toString());
      const { height } = ctx.element;

      const valueHeightThresholds = {
        WITH_SPACE: 50,
        NORMAL: 40
      };

      return [
        category,
        ...(height > valueHeightThresholds.WITH_SPACE) ? [''] : [],
        ...(height > valueHeightThresholds.NORMAL) ? [value] : []
      ];
    };

    const config = {
      key: 'y',
      groups: ['x'],
      barPercentage: segmentAxis === 'X' ? 0.9 : 0.4, // default is 0.9
      categoryPercentage: segmentAxis === 'X' ? 0.8 : 1, // default is 0.8
      labels: {
        display: true,
        padding: 15,
        align: 'left',
        position: 'top',
        color: 'white',
        font: { family: 'Poppins', size: 11, weight: 500 },
        formatter: formatLabel
      },
      data: chartData,
      ...DEFAULT_DATASET_CONFIG,
      ...dataset.style,
      label:
        ((xAxisType !== 'SINGLE_NON_TIME_FIELD' || dataset.seriesType === 'LINE') &&
          dataset.label) ||
        undefined,
      dimensionFieldNames: getDimensionFieldNamesFromDataSet(dataset),
      periodOverPeriod
    };

    if (seriesType && SUPPORTED_CHART_TYPES.includes(seriesType.toLowerCase())) {
      config.type = seriesType.toLowerCase();
    }

    return config;
  });
};

const normalizeKpiBarsDatasets = (config, basicConfig) => {
  if (
    config.xAxisType !== X_AXIS_TYPE.SIMPLE_KPI ||
    config.series[0].seriesType === CHART_TYPES.FUNNEL.toUpperCase()
  ) {
    return basicConfig;
  }

  const basicConfigCopy = cloneDeep(basicConfig);
  const { datasets } = basicConfigCopy.data;
  const [firstDataset] = datasets;
  datasets.slice(1).forEach(dataset => {
    const [datasetData] = dataset.data;
    firstDataset.data.push(datasetData);
  });
  basicConfigCopy.data.datasets = [firstDataset];
  return basicConfigCopy;
};

export default (config, chartJSConfig) => {
  let datasets = extractDatasets(config);
  const showAllSegments = config.dashboardOptions?.charts?.showAllLegendItems || false;
  if (!showAllSegments && config.xAxisType !== X_AXIS_TYPE.SIMPLE_KPI) {
    datasets = datasets.map(limitDisplayedDatasets);
  }

  const type = [
    CHART_TYPES.HORIZONTAL_BAR.toUpperCase(),
    CHART_TYPES.BAR.toUpperCase(),
  ].includes(config.series[0].seriesType) ? 'bar' : 'line';

  let basicConfig = {
    type,
    data: { datasets }
  };

  basicConfig = normalizeKpiBarsDatasets(config, basicConfig);
  basicConfig = merge(basicConfig, config.style);

  return merge(chartJSConfig, basicConfig);
};
